import { getTruthyValue } from "@draftkings/sportsbook-common-utils";
import { parsePlayerBonus, getBoostPercentage } from "../parsePlayerBonus";
import { parseOdds } from "../parseOdds";
import { formatDate } from "../formatDate";
import { parseSelections, parseBetEnhancedType, parseCombinations, getSelectionsLength, buildProgressiveParlayTable, } from "./utils";
import { isSettledStatus, isSettlementStatusVoid, getTeamIconUrl, safeString } from "../../helpers";
import { parseSettlementStatus, parseSettlementStatusesByBonusesAndTags } from "../parseSettlementStatus";
import { getStatusHandler, } from "../../constants";
import { parseBetReturns } from "./parseBetReturns";
import { parseReplayData } from "../parseReplayData";
import { parseTags } from "../parseTags";
const betMap = {
    Single: parseSingleBet,
    Parlay: parseParlayBet,
    ProgressiveParlay: parseProgressiveParlayBet,
    RoundRobin: parseRoundRobinBet,
    YourBet: parseSGPBet,
    Replay: parseReplayBet,
    Moonshot: parseMoonshotBet,
};
const winningCelebrationStatuses = ["Won", "CashOut", "DeadHeatReduction", "GhostWin"];
export function parseBet(options) {
    const { bet, events, teamLogos, dateFormatter, dictionary, staticS3Host, swooshFeatureFlag, swooshBaseUrl } = options;
    const parser = betMap[bet.type];
    const selections = parseSelections({
        selections: bet.selections,
        events,
        teamLogos,
        dateFormatter,
        dict: dictionary,
        staticS3Host,
        swooshFeatureFlag,
        swooshBaseUrl
    });
    const statusLabelMap = new Proxy({
        Unknown: dictionary.myBetsDictionary.Unknown,
        Open: dictionary.myBetsDictionary.Open,
        Won: dictionary.myBetsDictionary.Won,
        Lost: dictionary.myBetsDictionary.Lost,
        CashOut: dictionary.myBetsDictionary.CashOutStatus,
        Cancelled: dictionary.myBetsDictionary.Cancelled,
        DeadHeatReduction: dictionary.myBetsDictionary.Won,
        Void: dictionary.myBetsDictionary.Void,
        GhostWin: dictionary.myBetsDictionary.GhostWin,
        EarlyExit: dictionary.myBetsDictionary.EarlyExitFull,
        EarlyExitWin: dictionary.myBetsDictionary.EarlyExitWin,
    }, getStatusHandler);
    if (typeof parser === "function" && selections.length > 0) {
        return parser(Object.assign(Object.assign({}, options), { statusLabelMap, selections }));
    }
    return undefined;
}
function parseCommonBet({ bet, selections, cashOut, dictionary, formatAmount, dateFormatter, statusLabelMap, openModal, openPromosPage, retriever, openBetOverMaxAmountModal, openEarlyWinModal, locale }) {
    var _a, _b, _c, _d, _e;
    const settlementStatus = parseSettlementStatus(bet.settlementStatus);
    const playerBonus = parsePlayerBonus({
        bet,
        dictionary,
        retriever,
        openModal,
        openPromosPage,
        formatAmount,
        openBetOverMaxAmountModal,
        openEarlyWinModal,
        locale,
    });
    const status = parseSettlementStatusesByBonusesAndTags(settlementStatus, (_a = bet.bonus) === null || _a === void 0 ? void 0 : _a.boostStatus, bet.earlyExitStatus);
    return Object.assign(Object.assign({ betId: bet.betId, receiptId: `${dictionary.myBetsDictionary.DK}${bet.receiptId}`, purchaseId: bet.receiptId, enhancedType: parseBetEnhancedType(selections), title: "", subtitle: selections.map(s => s.selectionDisplayName).join(", ") }, parseOdds(bet, playerBonus)), { formattedPlacementDate: formatDate(bet.placementDate, dateFormatter), formattedReturns: parseBetReturns({ bet, playerBonus, boostPercentage: getBoostPercentage((_b = bet.bonus) === null || _b === void 0 ? void 0 : _b.boostValue, (_c = bet.bonus) === null || _c === void 0 ? void 0 : _c.boostOriginalValue), dictionary, formatAmount }), formattedStake: formatAmount(bet.stake, `${dictionary.myBetsDictionary.Wager}${dictionary.myBetsDictionary.AmountDisplayString}`), playerBonus,
        selections,
        status,
        cashOut, statusLabel: ((_d = bet.bonus) === null || _d === void 0 ? void 0 : _d.boostStatus) === "GuaranteedPayoutWon" ? dictionary.myBetsDictionary.GhostWin : statusLabelMap[status], showWinningCelebration: winningCelebrationStatuses.includes(status), tag: ((_e = bet.tags) === null || _e === void 0 ? void 0 : _e.includes(1)) || bet.earlyExitStatus !== "None" ? parseTags(bet, dictionary) : undefined });
}
function parseSingleBet(options) {
    var _a, _b, _c;
    const commonBetData = parseCommonBet(options);
    const [selection] = options.selections;
    const event = options.events.get(((_a = selection.event) === null || _a === void 0 ? void 0 : _a.eventId) || "");
    const leagueId = getTruthyValue(event === null || event === void 0 ? void 0 : event.leagueId, "");
    const participant = (_b = event === null || event === void 0 ? void 0 : event.participants) === null || _b === void 0 ? void 0 : _b.find(p => p.name === selection.selectionDisplayName);
    const iconUrl = getTeamIconUrl({
        swooshFeatureFlag: options.swooshFeatureFlag,
        swooshBaseUrl: options.swooshBaseUrl,
        rosettaTeamId: (_c = participant === null || participant === void 0 ? void 0 : participant.metadata) === null || _c === void 0 ? void 0 : _c.rosettaTeamId,
        staticS3Host: options.staticS3Host,
        teamLogos: options.teamLogos,
        leagueId,
        teamName: selection.selectionDisplayName,
        isNationalTeam: participant === null || participant === void 0 ? void 0 : participant.isNationalTeam,
        countryCode: participant === null || participant === void 0 ? void 0 : participant.countryCode
    });
    return Object.assign(Object.assign({}, commonBetData), { betType: "Single", title: selection.selectionDisplayName, subtitle: selection.marketDisplayName, iconUrl, selections: [selection] });
}
function parseReplayBet(options) {
    const commonBetData = parseCommonBet(options);
    const { selections, dictionary } = options;
    const [selection] = selections;
    const { pitchNumber } = selection.replayData || {};
    const replayData = parseReplayData(options.bet.replayData, pitchNumber, options.formatDateToFullMonthDayYear, dictionary);
    const subtitle = pitchNumber ? `${selection.marketDisplayName}: ${dictionary.myBetsDictionary.Pitch} ${pitchNumber}` : selection.marketDisplayName;
    return Object.assign(Object.assign({}, commonBetData), { betType: "Single", enhancedType: "Replay", title: selection.selectionDisplayName, subtitle, selections: [selection], replayData });
}
function parseParlayBet(options) {
    const commonBetData = parseCommonBet(options);
    const { selections, dictionary } = options;
    return Object.assign(Object.assign({}, commonBetData), { betType: "Parlay", title: `${getSelectionsLength(selections)} ${dictionary.myBetsDictionary.PicksParlay}` });
}
function parseProgressiveParlayBet(options) {
    const commonBetData = parseCommonBet(options);
    const { dictionary, bet } = options;
    const activeSelections = bet.selections.filter(selection => !isSettlementStatusVoid(parseSettlementStatus(selection.settlementStatus)));
    const settled = bet.selections.filter(s => isSettledStatus(parseSettlementStatus(s.settlementStatus)));
    return Object.assign(Object.assign({}, commonBetData), { betType: "Parlay", title: `${getSelectionsLength(bet.settlementStatus === "Cancelled" ? bet.selections : activeSelections)} ${dictionary.myBetsDictionary.ProgressiveParlay}`, enhancedType: "Progressive", progressiveParlayData: {
            statusLabelSelections: `${settled.length}/${bet.numberOfSelections} ${dictionary.myBetsDictionary.Bets} ${dictionary.myBetsDictionary.Settled}`,
            voidedSelectionsText: bet.numberOfSelections !== activeSelections.length ? dictionary.myBetsDictionary.ProgressiverParlayVoidedSelecitonText : "",
            table: buildProgressiveParlayTable(bet, dictionary, options.formatAmount),
            originalSelections: !isSettlementStatusVoid(parseSettlementStatus(bet.settlementStatus)) && bet.numberOfSelections !== activeSelections.length ? bet.numberOfSelections : 0,
        } });
}
function parseRoundRobinBet(options) {
    const commonBetData = parseCommonBet(options);
    const { selections, bet, formatAmount, dictionary } = options;
    const combinations = parseCombinations({
        selections,
        combinations: bet.combinations,
        dictionary,
        formatAmount
    });
    const settledCombinations = combinations.filter(s => isSettledStatus(s.status));
    return Object.assign(Object.assign({}, commonBetData), { betType: "RoundRobin", title: `(${getSelectionsLength(selections)}) ${dictionary.myBetsDictionary.RoundRobin}`, combinations, selections: selections.map((s, index) => (Object.assign(Object.assign({}, s), { selectionDisplayName: `${index + 1}. ${s.selectionDisplayName}` }))), statusLabel: `${settledCombinations.length}/${combinations.length} ${dictionary.myBetsDictionary.Bets} ${dictionary.myBetsDictionary.Settled}`, showWinningCelebration: combinations.filter(s => winningCelebrationStatuses.includes(s.status)).length > 0, cashOut: undefined });
}
function parseSGPBet(options) {
    const commonBetData = parseCommonBet(options);
    const { selections, dictionary } = options;
    return Object.assign(Object.assign({}, commonBetData), { betType: "YourBet", title: `${getSelectionsLength(selections)} ${dictionary.myBetsDictionary.Picks}` });
}
function parseMoonshotBet(options) {
    const commonBetData = parseCommonBet(options);
    const { bet, dictionary } = options;
    const [selection] = options.selections;
    const isOpenOrVoided = commonBetData.status === "Open" || isSettlementStatusVoid(commonBetData.status);
    const upToLabel = dictionary.myBetsDictionary.UpTo;
    const multiplierDisplay = isOpenOrVoided
        ? `${upToLabel} ${safeString(bet.displayOdds)}x`
        : `${safeString(bet.displayOdds)}x`;
    return Object.assign(Object.assign({}, commonBetData), { betType: "Moonshot", title: selection.selectionDisplayName, subtitle: selection.marketDisplayName, selections: [selection], originalDisplayOdds: multiplierDisplay, newOdds: undefined, newOddsVariant: undefined, cashOut: undefined });
}
//# sourceMappingURL=parseBet.js.map